import api from '@/services/apiService';
import {
  getCookie,
  setCookie,
  removeCookie,
  AUTH_COOKIE_NAMES,
  setSessionHint,
  clearSessionHint,
  getAccessToken,
  hasSessionHint,
} from '@/utils/auth';
import { getDeviceType, getOrCreateDeviceId } from '@/utils/device';

// Auth cookie name for community users
export const AUTH_COMMUNITY_COOKIE_NAME = 'auth_community_token';

// LocalStorage keys
const COMMUNITY_USER_KEY = 'communityUser';
const COMMUNITY_NAME_KEY = 'communityName';
const COMMUNITY_NICKNAME_KEY = 'communityNickname';
const COMMUNITY_AVATAR_KEY = 'communityAvatar';

// ==============================================
// TYPE DEFINITIONS
// ==============================================

export interface RegisterCommunityPayload {
  name: string;
  email: string;
  nickname: string;
  password: string;
  confirmPassword: string;
  phone?: string;
}

export interface LoginCommunityPayload {
  email: string;
  password: string;
  device_type?: string;
}

export interface CommunityUser {
  id: number;
  user_id: number;
  name: string;
  email: string;
  nickname: string;
  avatar?: string;
  phone?: string;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommunityProfilePayload {
  name: string;
  sport_category: string;
  location: string;
  slug?: string;
  description?: string;
  link_group?: string;
  instagram?: string;
  website?: string;
  tiktok?: string;
  youtube?: string;
  is_public: boolean;
  logo?: File | null;
  cover?: File | null;
  gallery?: File[];
  keep_assets?: {
    logo?: string;
    cover?: string;
    gallery?: string[];
  };
}

export interface CommunityAuthResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    refresh_token?: string;
    exp: number;
    user: {
      id: number;
      username: string;
      email: string;
      role: string;
      name: string;
    };
    community_user: CommunityUser;
    avatar: string; // Logo from community profile
  };
}

export interface OperationalCost {
  name: string;
  amount: number;
}

export interface PlayerRole {
  role: string;
  slots: number;
  price: number;
}

export interface SparringTeam {
  name: string;
  jersey_color: string;
}

export interface MabarEventConfig {
  player_roles: PlayerRole[];
  auto_confirm: boolean;
  allow_guest: boolean;
  max_guests_per_user: number;
  operational_costs?: OperationalCost[];
  payment_time?: 'awal' | 'akhir';
  payment_method?: 'manual' | 'qris';
  cutoff_hours?: number;
  waiting_list?: boolean;
  waiting_list_slots?: number;
  is_sparring?: boolean;
  teams?: SparringTeam[];
  facilities?: string[];
  time_zone?: 'WIB' | 'WITA' | 'WIT';
}

export const MABAR_FACILITIES_OPTIONS = [
  { id: 'air-minum', label: 'Air Minum' },
  { id: 'snack', label: 'Snack' },
  { id: 'p3k', label: 'P3K' },
  { id: 'jersey', label: 'Jersey' },
  { id: 'rompi', label: 'Rompi' },
  { id: 'sarung-tangan', label: 'Sarung Tangan' },
  { id: 'wasit', label: 'Wasit' },
  { id: 'raket', label: 'Raket' },
  { id: 'fotografer', label: 'Fotografer' },
  { id: 'videografer', label: 'Videografer' },
  { id: 'slowmotion', label: 'Slowmotion' },
  { id: 'drone', label: 'Drone' },
  { id: 'lainnya', label: 'Lainnya' },
];

// ==============================================
// COMMUNITY MEMBER TYPE DEFINITIONS
// ==============================================

export interface CommunityMemberUser {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
  nickname?: string;
  username?: string;
}

export interface CommunityMember {
  id: number;
  community_id: number;
  community_user_id: number;
  position: 'admin' | 'member';
  is_approved: boolean;
  join_date: string;
  created_at: string;
  community_user?: CommunityMemberUser;
  event_count?: number;
  match_count?: number;
  keaktifan_percentage?: number;
  win_rate?: number;
  goal_count?: number;
  gol_count?: number;
  assist_count?: number;
  save_count?: number;
  last_event_name?: string;
  last_event_date?: string;
}

export interface GetCommunityMemberStatsResponse {
  success: boolean;
  message: string;
  data: {
    active_count: number;
    new_this_month: number;
    total_members: number;
  };
}

export interface JoinCommunityResponse {
  success: boolean;
  message: string;
  data: CommunityMember;
}

export interface LeaveCommunityResponse {
  success: boolean;
  message: string;
}

export interface CheckMembershipResponse {
  success: boolean;
  is_member: boolean;
  is_host: boolean;
}

export interface MyCommunityMemberPreview {
  name: string;
  avatar?: string | null;
}

export interface MyCommunityItem {
  slug: string;
  name: string;
  logo: string;
  sportCategoryName: string;
  sportCategoryIcon: string;
  location: string;
  totalMembers: number;
  membersPreview: MyCommunityMemberPreview[];
  is_verified?: boolean;
}

export interface GetMyCommunitiesResponse {
  success: boolean;
  data: MyCommunityItem[];
  meta?: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface GetCommunityMembersResponse {
  success: boolean;
  message: string;
  data: {
    members: CommunityMember[];
    total_count: number;
    page?: number;
    limit?: number;
  };
}

// ==============================================
// COMMUNITY DASHBOARD TYPES
// ==============================================

export interface CommunityDashboardUpcomingEvent {
  id: number;
  name: string;
  slug: string;
  event_date: string;
  start_time: string;
  end_time: string;
  sport_category: string;
  sport_category_id: number;
  venue_name?: string;
  quota_filled?: number;
  quota_total?: number;
  is_paid?: boolean;
}

export interface CommunityDashboardUpcomingDay {
  date: string;
  date_label: string;
  events: CommunityDashboardUpcomingEvent[];
}

export interface ChartDataPoint {
  label: string;
  value: number;
}

export interface CommunityDashboardResponse {
  total_member: number;
  member_joined_this_month?: number;
  event_revenue: number;
  event_revenue_total?: number;
  event_revenue_prev_month?: number;
  revenue_growth_percent?: number;
  last_month_name?: string;
  event_30_ended: number;
  events_ended_total?: number;
  events_this_month?: number;
  event_30_total: number;
  event_mabar_total: number;
  sparing_upcoming: number;
  total_visits?: number;
  avg_visits_per_event?: number;
  upcoming_activities: CommunityDashboardUpcomingDay[];
  recent_activities: unknown[];
  trend_monthly_members?: ChartDataPoint[];
  trend_monthly_revenue?: ChartDataPoint[];
  trend_weekly_members?: ChartDataPoint[];
  trend_weekly_revenue?: ChartDataPoint[];
}

// ==============================================
// MABAR EVENT TYPE DEFINITIONS
// ==============================================
// CUSTOM FORM TYPES
// ==============================================

export type CustomFormFieldType = 'text' | 'number' | 'date' | 'option';

export interface CustomFormField {
  id: string;
  title: string;
  type: CustomFormFieldType;
  required: boolean;
  options?: string[];
  template_type?: 'form_role' | 'team' | 'role' | 'player_number' | 'level' | null;
  template?: 'form_role' | 'team' | 'player_position' | 'role' | 'player_number' | 'level' | string | null;
  read_only_options?: boolean;
}

export interface CustomFormAnswerItem {
  field_id?: string;
  question_id?: string;
  title?: string;
  type?: CustomFormFieldType;
  value: string | number | boolean;
  template_type?: 'form_role' | 'team' | 'role' | 'player_number' | 'level' | null;
}

export interface CreateMabarEventPayload {
  name: string;
  slug?: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  city: string;
  address: string;
  lokasi_arena: string;
  map_link: string;
  sport_category_slug: string;
  visibility: 'public' | 'private';
  type: 'free' | 'paid';
  tnc?: string;
  link_group?: string;
  config: MabarEventConfig;
  custom_form?: CustomFormField[];
  status?: string;
}

export interface UpdateMabarEventPayload extends Partial<CreateMabarEventPayload> {}

export interface MabarEvent {
  id: number;
  slug: string;
  name: string;
  description?: string;
  event_date: string;
  start_time: string;
  end_time: string;
  city: string;
  address: string;
  lokasi_arena?: string;
  map_link?: string;
  sport_category: {
    id: number;
    name: string;
    slug: string;
    icon?: string;
  };
  visibility: 'public' | 'private';
  type: 'free' | 'paid';
  status: string;
  is_host: boolean;
  total_slots: number;
  filled_slots: number;
  available_slots: number;
  slot_percentage: number;
  config: MabarEventConfig;
  custom_form?: CustomFormField[];
  link_group?: string;
  tnc?: string;
  host: {
    name: string;
    avatar?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface MabarParticipant {
  id: number;
  event_id: number;
  user_id: number;
  name: string;
  avatar?: string;
  role: string;
  team?: string;
  price: number;
  email?: string;
  wa_number?: string;
  status: 'pending' | 'confirmed' | 'canceled';
  joined_at: string;
  confirmed_at?: string;
  canceled_at?: string;
  position?: string;
  custom_form_answer?: CustomFormAnswerItem[];
}

export interface AddParticipantPayload {
  name: string;
  role: string;
  team?: string;
  email?: string;
  wa_number?: string;
  status?: 'pending' | 'confirmed' | 'canceled';
  custom_form_answer?: CustomFormAnswerItem[];
}

export interface UpdateParticipantPayload {
  name?: string;
  email?: string;
  wa_number?: string;
  role?: string;
  status?: 'pending' | 'confirmed' | 'canceled';
  team?: string;
  custom_form_answer?: CustomFormAnswerItem[];
}

// Game list item (by event); home_score/away_score derived from timeline
export interface GameListItem {
  id: number;
  slug: string;
  home_team_name: string;
  away_team_name: string;
  start_time?: string | null;
  end_time?: string | null;
  home_score?: number;
  away_score?: number;
  scorers_summary?: string;
  timeline?: unknown[];
}

export interface StandingItem {
  position: number;
  team: string;

  matches_played: number;

  wins: number;
  draws: number;
  losses: number;

  goals_for: number;
  goals_against: number;
  goal_difference: number;

  points: number;
}

// Lineup entry for create game
export interface CreateGameLineupEntry {
  participant_id: number;
  is_starting: boolean;
  player_number?: number | null;
  role?: string;
}

export interface CreateGamePayload {
  home_team_name: string;
  away_team_name: string;
  home_lineup: CreateGameLineupEntry[];
  away_lineup: CreateGameLineupEntry[];
  /** JSON string e.g. {"format":"5:5"} for playing_style */
  playing_style?: string;
}

// Game detail (GET by slug) - lineups with player info
export interface GameLineupPlayerItem {
  id: number;
  player_participant_id: number;
  team_side: string;
  is_starting: boolean;
  player_number?: number | null;
  name: string;
  avatar?: string | null;
  role: string;
  position?: string;
  is_captain?: boolean;
  mmr_change?: number;
}

/** Timeline event as stored in mabar.games.timeline (JSONB) */
export interface GameTimelineEvent {
  time: string;
  type: string;
  team_side: string;
  player_id?: number;
  assist_id?: number | null;
  detail?: string | null;
  /** For type "substitution" */
  player_id_out?: number;
  player_id_in?: number;
}

export interface GameDetailResponse {
  id: number;
  slug: string;
  event_id: number;
  event_slug?: string;
  home_team_name: string;
  away_team_name: string;
  start_time?: string | null;
  end_time?: string | null;
  /** JSON string e.g. {"format":"5:5"} - backend returns default for old data (futsal 5:5, mini-soccer 7:7) */
  playing_style?: string | null;
  timeline?: GameTimelineEvent[] | unknown;
  lineups: GameLineupPlayerItem[];
  is_finalized?: boolean;
}

// Same shape as create; backend UpdateGameRequest accepts optional fields
export type UpdateGamePayload = CreateGamePayload;

export interface GenerateGamesBulkPayload {
  sort_by: 'join_time' | 'alphabet' | 'random';
  players_per_team: number;
  num_teams: number;
  num_games: number;
}

export interface GenerateGamesBulkGameItem {
  id: number;
  slug: string;
  home_team_name: string;
  away_team_name: string;
}

export interface GenerateGamesBulkResponse {
  games: GenerateGamesBulkGameItem[];
}

// ==============================================
// COMMUNITY AUTH SERVICE
// ==============================================

class CommunityService {
  constructor() {
    // Auto-initialize auth header on service creation
    this.init();
  }

  // ==============================================
  // AUTHENTICATION METHODS
  // ==============================================

  async register(payload: RegisterCommunityPayload): Promise<CommunityAuthResponse> {
    const res = await api.post('/auth/register-community', {
      ...payload,
      device_id: getOrCreateDeviceId(),
    });
    const response: CommunityAuthResponse = res.data;

    if (!response?.success || !response?.data?.token) {
      throw new Error(response?.message || 'Registration failed');
    }

    // Store token and user info
    this.setTokenCookie(response.data.token, response.data.exp);
    setSessionHint();
    this.setAuthHeader(response.data.token);
    this.setCommunityUser(response.data.community_user, response.data.avatar);

    return response;
  }

  async login(payload: LoginCommunityPayload): Promise<CommunityAuthResponse> {
    const res = await api.post('/auth/login-community', {
      ...payload,
      device_type: getDeviceType(),
      device_id: getOrCreateDeviceId(),
    });
    const response: CommunityAuthResponse = res.data;

    if (!response?.success || !response?.data?.token) {
      throw new Error(response?.message || 'Login failed');
    }

    // Store token and user info
    this.setTokenCookie(response.data.token, response.data.exp);
    setSessionHint();
    this.setAuthHeader(response.data.token);
    this.setCommunityUser(response.data.community_user, response.data.avatar);
    this.setRole(response.data.user.role || '');

    return response;
  }

  async googleLogin(token: string, code?: string): Promise<any> {
    const res = await api.post('/auth/google', {
      token,
      role: 'community',
      code,
      device_type: getDeviceType(),
      device_id: getOrCreateDeviceId(),
    });
    const response = res.data;

    if (!response?.success || !response?.data?.token) {
      throw new Error(response?.message || 'Google login failed');
    }

    // Store token and user info
    this.setTokenCookie(response.data.token, response.data.exp);
    setSessionHint();
    this.setAuthHeader(response.data.token);

    // Map AuthResult back to community session
    if (response.data.community_user) {
      this.setCommunityUser(response.data.community_user, response.data.avatar || '');
    } else if (response.data.client_name) {
      // Fallback for community users without community_user field
      const communityUser: CommunityUser = {
        id: response.data.user_id || 0,
        user_id: response.data.user_id || 0,
        name: response.data.client_name,
        email: response.data.email || '',
        nickname: response.data.client_name,
        avatar: response.data.avatar || undefined,
        is_verified: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      this.setCommunityUser(communityUser, response.data.avatar || '');
    }

    return response;
  }

  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    const res = await api.get(`/auth/verify-email-community?token=${token}`);
    return res.data;
  }

  async resendVerification(email: string): Promise<{ success: boolean; message: string }> {
    const res = await api.post('/auth/resend-verification-community', { email });
    return res.data;
  }

  async logout() {
    clearSessionHint();
    try {
      // @ts-ignore
      await api.post('/auth/logout', {}, { _skipInterceptor: true });
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      this.removeTokenCookie();
      this.clearCommunityUser();
      this.clearAuthHeader();
      localStorage.removeItem('emailVerified');
      localStorage.removeItem('communityName');
      localStorage.removeItem('communityAvatar');
    }
  }

  getToken(): string | null {
    return getAccessToken() || getCookie(AUTH_COMMUNITY_COOKIE_NAME) || getCookie('auth:app');
  }

  getRefreshToken(): string | null {
    return getCookie(AUTH_COOKIE_NAMES.COMMUNITY_REFRESH);
  }

  isAuthenticated(): boolean {
    return !!this.getToken() || !!this.getRefreshToken() || hasSessionHint();
  }

  async refreshToken(): Promise<string> {
    // We can't check for refresh token presence in JS (HttpOnly).
    // Just try to call API.

    try {
      // @ts-ignore
      const res = await api.post(
        '/auth/refresh',
        { device_id: getOrCreateDeviceId() },
        {
          // @ts-ignore
          _skipInterceptor: true,
        }
      );
      const response: any = res.data;

      if (!response?.success || !response?.data?.token) {
        throw new Error('Refresh failed');
      }

      this.setTokenCookie(response.data.token, response.data.exp);
      setSessionHint();
      this.setAuthHeader(response.data.token);

      return response.data.token;
    } catch (error) {
      this.logout();
      throw error;
    }
  }

  getCommunityUser(): CommunityUser | null {
    const stored = localStorage.getItem('community_user');
    return stored ? JSON.parse(stored) : null;
  }

  // Initialize auth header from stored token
  init(): void {
    const token = this.getToken();
    if (token) {
      this.setAuthHeader(token);
    }
  }

  // ==============================================
  // MABAR EVENT CRUD METHODS
  // ==============================================

  async createEvent(payload: CreateMabarEventPayload): Promise<MabarEvent> {
    const res = await api.post('/community/mabar/events', payload);
    return res.data.data;
  }

  async getMyEvents(params?: {
    page?: number;
    limit?: number;
    status?: string;
    sport?: string;
    search?: string;
    tab?: string;
  }): Promise<{ events: MabarEvent[]; total: number; page: number; limit: number }> {
    const res = await api.get('/community/mabar/my-events', { params });
    return res.data.data;
  }

  async getEventBySlug(slug: string): Promise<MabarEvent> {
    const res = await api.get(`/community/mabar/events/${slug}`);
    return res.data.data;
  }

  async updateEvent(slug: string, payload: UpdateMabarEventPayload): Promise<MabarEvent> {
    const res = await api.put(`/community/mabar/events/${slug}`, payload);
    return res.data.data;
  }

  async cancelEvent(slug: string): Promise<void> {
    await api.put(`/community/mabar/events/${slug}/cancel`);
  }

  async deleteEvent(slug: string): Promise<void> {
    await api.delete(`/community/mabar/events/${slug}`);
  }

  // ==============================================
  // PARTICIPANT MANAGEMENT METHODS
  // ==============================================

  async getEventParticipants(
    slug: string,
    params?: { page?: number; limit?: number; status?: string }
  ): Promise<{ participants: MabarParticipant[]; total: number; page: number; limit: number }> {
    const res = await api.get(`/community/mabar/events/${slug}/participants`, { params });
    return res.data.data;
  }

  async addParticipant(slug: string, payload: AddParticipantPayload): Promise<MabarParticipant> {
    const res = await api.post(`/community/mabar/events/${slug}/participants`, payload);
    return res.data.data;
  }

  async updateParticipant(
    slug: string,
    participantId: number,
    payload: UpdateParticipantPayload
  ): Promise<MabarParticipant> {
    const res = await api.put(
      `/community/mabar/events/${slug}/participants/${participantId}`,
      payload
    );
    return res.data.data;
  }

  async updateParticipantBadmintonLevel(
    slug: string,
    participantId: number,
    level: string
  ): Promise<void> {
    await api.put(`/community/mabar/events/${slug}/participants/${participantId}/badminton-level`, {
      level,
    });
  }

  async updateParticipantStatus(
    slug: string,
    participantId: number,
    status: 'pending' | 'confirmed' | 'canceled'
  ): Promise<void> {
    await api.patch(`/community/mabar/events/${slug}/participants/${participantId}/status`, {
      status,
    });
  }

  async deleteParticipant(slug: string, participantId: number): Promise<void> {
    await api.delete(`/community/mabar/events/${slug}/participants/${participantId}`);
  }

  async generateParticipantQRIS(
    slug: string,
    participantId: number
  ): Promise<{
    success: boolean;
    message: string;
    order_number: string;
    invoice_url?: string;
    payload?: any;
  }> {
    const res = await api.post(`/community/mabar/events/${slug}/participants/${participantId}/qris`);
    return res.data;
  }

  // ==============================================
  // GAME (MABAR EVENTS) - uses /mabar with community token when on /community
  // ==============================================

  async getGamesByEventSlug(slug: string): Promise<GameListItem[]> {
    const res = await api.get(`/mabar/events/${slug}/games`);
    return res.data?.data ?? [];
  }

  async getStandingsByEventSlug(slug: string): Promise<StandingItem[]> {
    const res = await api.get(`/mabar/events/${slug}/standings`);
    return res.data?.data ?? [];
  }

  async createGame(
    eventSlug: string,
    payload: CreateGamePayload
  ): Promise<{ id: number; slug: string; [key: string]: unknown }> {
    const res = await api.post(`/mabar/events/${eventSlug}/games`, payload);
    return res.data?.data;
  }

  async generateGamesBulk(
    eventSlug: string,
    payload: GenerateGamesBulkPayload
  ): Promise<GenerateGamesBulkResponse> {
    const res = await api.post<{ data?: GenerateGamesBulkResponse; message?: string }>(
      `/mabar/events/${eventSlug}/games/generate-bulk`,
      payload
    );
    if (res.data?.data == null) {
      throw new Error((res.data as { message?: string })?.message ?? 'Gagal generate jadwal');
    }
    return res.data.data;
  }

  async getGameBySlug(slug: string): Promise<GameDetailResponse> {
    const res = await api.get(`/mabar/games/${slug}`);
    return res.data?.data;
  }

  async updateGame(gameSlug: string, payload: UpdateGamePayload): Promise<GameDetailResponse> {
    const res = await api.patch(`/mabar/games/${gameSlug}`, payload);
    return res.data?.data;
  }

  async deleteGame(gameSlug: string): Promise<void> {
    await api.delete(`/mabar/games/${gameSlug}`);
  }

  async updateGameTimeline(gameSlug: string, timeline: GameTimelineEvent[]): Promise<void> {
    await api.patch(`/mabar/games/${gameSlug}/timeline`, { timeline });
  }

  async postSubstitution(
    gameSlug: string,
    payload: {
      time: string;
      team_side: string;
      player_id_out: number;
      player_id_in: number;
    }
  ): Promise<GameDetailResponse> {
    const res = await api.post(`/mabar/games/${gameSlug}/substitution`, payload);
    return res.data?.data;
  }

  // ==============================================
  // DASHBOARD & EXPORT (COMMUNITY AUTH)
  // ==============================================

  async getEventDashboard(
    slug: string,
    page: number = 1,
    limit: number = 10,
    search: string = '',
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ): Promise<any> {
    const params: Record<string, unknown> = { page, limit, search };
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    const res = await api.get(`/community/mabar/my-events/${slug}/dashboard`, {
      params,
    });
    return res.data.data;
  }

  async getCommunityTransactions(
    page: number = 1,
    limit: number = 10,
    search: string = ''
  ): Promise<any> {
    const params: Record<string, unknown> = { page, limit, search };
    const res = await api.get(`/community/mabar/transactions`, {
      params,
    });
    return res.data.data;
  }

  async exportEventPdf(slug: string): Promise<Blob> {
    const res = await api.get(`/community/mabar/my-events/${slug}/export/pdf`, {
      responseType: 'blob',
    });
    return res.data;
  }

  async exportEventXlsx(slug: string): Promise<Blob> {
    const res = await api.get(`/community/mabar/my-events/${slug}/export/xlsx`, {
      responseType: 'blob',
    });
    return res.data;
  }

  async updatePaymentStatus(
    slug: string,
    participantId: number,
    payment_status: 'paid' | 'unpaid'
  ): Promise<void> {
    await api.patch(
      `/community/mabar/my-events/${slug}/participants/${participantId}/payment-status`,
      { payment_status }
    );
  }

  // ==============================================
  // PRIVATE HELPER METHODS
  // ==============================================

  private setTokenCookie(token: string, exp: number) {
    const expires = new Date(exp * 1000).toUTCString();
    setCookie(AUTH_COMMUNITY_COOKIE_NAME, token, expires);
  }

  // private setRefreshTokenCookie(token: string) { ... } // Removed

  private removeTokenCookie() {
    removeCookie(AUTH_COMMUNITY_COOKIE_NAME);
  }

  // private removeRefreshTokenCookie() { ... } // Removed

  private setAuthHeader(token: string) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  private clearAuthHeader() {
    delete api.defaults.headers.common['Authorization'];
  }

  private setCommunityUser(user: CommunityUser, avatar?: string) {
    localStorage.setItem(COMMUNITY_USER_KEY, JSON.stringify(user));
    localStorage.setItem(COMMUNITY_NAME_KEY, user.name);
    localStorage.setItem(COMMUNITY_NICKNAME_KEY, user.nickname);
    // Store avatar from login response (community profile logo)
    if (avatar) {
      localStorage.setItem(COMMUNITY_AVATAR_KEY, avatar);
    } else {
      localStorage.removeItem(COMMUNITY_AVATAR_KEY);
    }
  }

  private clearCommunityUser() {
    localStorage.removeItem(COMMUNITY_USER_KEY);
    localStorage.removeItem(COMMUNITY_NAME_KEY);
    localStorage.removeItem(COMMUNITY_NICKNAME_KEY);
    localStorage.removeItem(COMMUNITY_AVATAR_KEY);
  }

  // ==============================================
  // COMMUNITY PROFILE METHODS
  // ==============================================

  async updateProfile(payload: CommunityProfilePayload): Promise<any> {
    const formData = new FormData();

    // Append required fields
    formData.append('name', payload.name);
    formData.append('sport_category', payload.sport_category);
    formData.append('location', payload.location);
    formData.append('is_public', String(payload.is_public));

    // Optional slug (min 3 chars, unique)
    if (payload.slug && payload.slug.trim()) {
      formData.append('slug', payload.slug.trim());
    }

    // Append optional text fields
    if (payload.description) formData.append('description', payload.description);
    if (payload.link_group) formData.append('link_group', payload.link_group);
    if (payload.instagram) formData.append('instagram', payload.instagram);
    if (payload.website) formData.append('website', payload.website);
    if (payload.tiktok) formData.append('tiktok', payload.tiktok);
    if (payload.youtube) formData.append('youtube', payload.youtube);

    // Append kept assets (existing assets to preserve)
    if (payload.keep_assets) {
      formData.append('keep_assets', JSON.stringify(payload.keep_assets));
    }

    // Append file uploads
    if (payload.logo) {
      formData.append('logo', payload.logo);
    }
    if (payload.cover) {
      formData.append('cover', payload.cover);
    }
    if (payload.gallery && payload.gallery.length > 0) {
      payload.gallery.forEach((file) => {
        formData.append('gallery', file);
      });
    }

    const response = await api.put('/community/profile', formData);

    return response.data;
  }

  async getProfile(): Promise<any> {
    const response = await api.get('/community/profile');
    return response.data;
  }

  /**
   * Check if a slug is available (min 3 chars). Use with debounce to avoid flooding server.
   */
  async checkSlugAvailability(slug: string): Promise<{ available: boolean }> {
    const response = await api.get('/community/profile/check-slug', {
      params: { slug: slug.trim() },
    });
    return response.data?.data ?? { available: false };
  }

  /**
   * Check if an event slug is available (min 3 chars). Use with debounce to avoid flooding server.
   */
  async checkEventSlugAvailability(slug: string): Promise<{ available: boolean; slug?: string }> {
    const response = await api.get('/community/mabar/events/check-slug', {
      params: { slug: slug.trim() },
    });
    return response.data?.data ?? { available: false };
  }

  async getPublicProfile(slug: string): Promise<any> {
    const response = await api.get(`/public/community/${slug}`);
    return response.data;
  }

  // ==============================================
  // ACCOUNT MANAGEMENT METHODS
  // ==============================================

  async updatePassword(payload: {
    current_password: string;
    new_password: string;
    confirm_new_password: string;
  }): Promise<any> {
    const response = await api.post('/community/update-password', payload);
    return response.data;
  }

  async updateBankDetails(payload: {
    bank_name: string;
    bank_account_number: string;
    bank_account_holder: string;
  }): Promise<any> {
    const response = await api.put('/community/bank-details', payload);
    return response.data;
  }

  async getBankDetails(): Promise<{
    bank_name: string;
    bank_account_number: string;
    bank_account_holder: string;
  }> {
    const response = await api.get('/community/bank-details');
    return response.data.data;
  }

  async getAccount(): Promise<{
    id: number;
    name: string;
    email: string;
    nickname: string;
    phone?: string;
    is_verified: boolean;
  }> {
    const response = await api.get('/community/account');
    return response.data.data;
  }

  async getCommunityDashboard(): Promise<CommunityDashboardResponse> {
    const response = await api.get('/community/dashboard');
    return response.data.data;
  }

  // ==============================================
  // COMMUNITY MEMBER METHODS
  // ==============================================

  /**
   * Join a community
   * @param slug - Community slug
   * @returns Response with member data
   */
  async joinCommunity(slug: string): Promise<JoinCommunityResponse> {
    // Authorization header will be automatically added by apiService interceptor
    const response = await api.post(`/client/community/${slug}/join`, {});
    return response.data;
  }

  /**
   * Leave a community
   * @param slug - Community slug
   * @returns Success response
   */
  async leaveCommunity(slug: string): Promise<LeaveCommunityResponse> {
    // Authorization header will be automatically added by apiService interceptor
    const response = await api.post(`/client/community/${slug}/leave`, {});
    return response.data;
  }

  /**
   * Check if current user is a member of a community
   * @param slug - Community slug
   * @returns Membership status
   */
  async checkMembership(slug: string): Promise<CheckMembershipResponse> {
    // Authorization header will be automatically added by apiService interceptor
    const response = await api.get(`/client/community/${slug}/check-membership`);
    return response.data;
  }

  /**
   * Get communities the current client has joined (Komunitas Saya)
   * @returns List of my communities with logo, sport category, location, members preview
   */
  async getMyCommunities(): Promise<GetMyCommunitiesResponse> {
    const response = await api.get<GetMyCommunitiesResponse>('/client/my-communities');
    return response.data;
  }

  /**
   * Get public communities that current client has NOT joined yet
   * @param paramsOrSearch - Optional search string or options object
   */
  async getDiscoverCommunities(
    paramsOrSearch?:
      | string
      | {
          search?: string;
          category?: string;
          location?: string;
          page?: number;
          limit?: number;
          sort_by?: string;
          sort?: string;
          sort_order?: string;
          order_by?: string;
          exclude_joined?: boolean;
          page_my_community?: boolean;
          is_my_community?: boolean;
        }
  ): Promise<GetMyCommunitiesResponse> {
    const params = new URLSearchParams();
    if (typeof paramsOrSearch === 'string') {
      if (paramsOrSearch) params.append('search', paramsOrSearch);
    } else if (paramsOrSearch) {
      if (paramsOrSearch.search) params.append('search', paramsOrSearch.search);
      if (paramsOrSearch.category) params.append('category', paramsOrSearch.category);
      if (paramsOrSearch.location) params.append('location', paramsOrSearch.location);
      if (paramsOrSearch.page) params.append('page', paramsOrSearch.page.toString());
      if (paramsOrSearch.limit) params.append('limit', paramsOrSearch.limit.toString());
      if (paramsOrSearch.sort_by) params.append('sort_by', paramsOrSearch.sort_by);
      if (paramsOrSearch.sort) params.append('sort', paramsOrSearch.sort);
      if (paramsOrSearch.sort_order) params.append('sort_order', paramsOrSearch.sort_order);
      if (paramsOrSearch.order_by) params.append('order_by', paramsOrSearch.order_by);
      if (paramsOrSearch.exclude_joined !== undefined) params.append('exclude_joined', paramsOrSearch.exclude_joined.toString());
      if (paramsOrSearch.page_my_community !== undefined) params.append('page_my_community', paramsOrSearch.page_my_community.toString());
      if (paramsOrSearch.is_my_community !== undefined) params.append('is_my_community', paramsOrSearch.is_my_community.toString());
    }

    // Default sorting query params for Backend (members_desc / ORDER BY total_members DESC)
    if (!params.has('sort_by')) {
      params.append('sort_by', 'members');
    }
    if (!params.has('sort')) {
      params.append('sort', 'desc');
    }
    if (!params.has('sort_order')) {
      params.append('sort_order', 'desc');
    }
    if (!params.has('order_by')) {
      params.append('order_by', 'total_members');
    }

    // Guest-safe public list when we do not need auth-only exclude_joined filters
    const needsAuthExclude =
      params.get('exclude_joined') === 'true' ||
      params.get('page_my_community') === 'true' ||
      params.get('is_my_community') === 'true';
    const path = needsAuthExclude
      ? `/client/discover-communities?${params.toString()}`
      : `/public/discover-communities?${params.toString()}`;

    const response = await api.get<GetMyCommunitiesResponse>(path);
    return response.data;
  }

  /**
   * Get community members (public)
   * @param slug - Community slug
   * @param limitOrParams - Optional limit or query params
   * @returns List of members
   */
  async getCommunityMembers(
    slug: string,
    limitOrParams?:
      | number
      | {
          page?: number;
          limit?: number;
          search?: string;
          position?: 'admin' | 'member';
          sort_by?: string;
          order?: 'asc' | 'desc';
        }
  ): Promise<GetCommunityMembersResponse> {
    const params =
      typeof limitOrParams === 'number' ? { limit: limitOrParams } : (limitOrParams ?? undefined);

    const response = await api.get(`/public/community/${slug}/members`, { params });
    return response.data;
  }

  /**
   * Get community member statistics overview
   * @param slug - Community slug
   */
  async getCommunityMemberStats(slug: string): Promise<GetCommunityMemberStatsResponse> {
    try {
      const response = await api.get(`/public/community/${slug}/member-stats`);
      return response.data;
    } catch {
      // Fallback response
      return {
        success: false,
        message: 'Failed to fetch stats',
        data: { active_count: 0, new_this_month: 0, total_members: 0 },
      };
    }
  }

  /**
   * Get detailed profile & activity statistics for a single community member
   * @param slug - Community slug
   * @param memberId - Member ID
   */
  async getCommunityMemberDetail(
    slug: string,
    memberId: number
  ): Promise<{ success: boolean; data: any }> {
    const response = await api.get(`/public/community/${slug}/members/${memberId}/detail`);
    return response.data;
  }

  /**
   * Get community profile stats (total event, match, member)
   * @param slug - Community slug
   */
  async getCommunityProfileStats(
    slug: string,
    params?: { sport?: string }
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      event_count: number;
      match_count: number;
      member_count: number;
      all_event_count?: number;
      all_match_count?: number;
    };
  }> {
    try {
      const response = await api.get(`/public/community/${slug}/stats`, { params });
      return response.data;
    } catch {
      return {
        success: true,
        message: 'OK',
        data: {
          event_count: 0,
          match_count: 0,
          member_count: 0,
          all_event_count: 0,
          all_match_count: 0,
        },
      };
    }
  }

  async rejectLeaveEventRequest(slug: string, participantEmail: string): Promise<void> {
    await api.post(`/community/mabar/reject-cancel`, {
      slug: slug,
      email: participantEmail,
    });
  }

  // Payment reminder methods
  async getUnpaidParticipantsCount(slug: string): Promise<{ count: number }> {
    const response = await api.get(`/community/mabar/events/${slug}/unpaid-count`);
    return response.data.data;
  }

  async notifyUnjoinedMembers(slug: string): Promise<{ sent: number }> {
    const response = await api.post(`/community/mabar/events/${slug}/notify-members`);
    return response.data.data;
  }

  async sendPaymentReminderBulk(slug: string): Promise<{ emails_sent: number }> {
    const response = await api.post(`/community/mabar/events/${slug}/payment-reminder/bulk`);
    return response.data.data;
  }

  async sendPaymentReminderSingle(slug: string, participantId: number): Promise<void> {
    await api.post(
      `/community/mabar/events/${slug}/participants/${participantId}/payment-reminder`
    );
  }

  private setRole(role: string) {
    localStorage.setItem('role', role);
  }
}

export default new CommunityService();
